import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { formatCr, monthLabel } from "@/lib/format";

/**
 * EPC Model Flat & SOH monthly grid — one row per (project, kind).
 * Values in ₹ Lakhs. Read/write for `epc` role + admin; read-only otherwise.
 * Feeds the Marketing tab (read-only) via GET /api/marketing/detail.
 */
export default function EpcModelSohGrid({ months }) {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "epc";

  const [rows, setRows] = useState([]);       // [{project, model_flat: {m:v}, soh: {m:v}}]
  const [dirty, setDirty] = useState({});      // {project::kind: {month: value}}
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newProject, setNewProject] = useState("");
  const [projects, setProjects] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: pj }] = await Promise.all([
        api.get("/outflows/epc/model-soh"),
        api.get("/projects"),
      ]);
      setRows(data?.rows || []);
      setProjects((pj || []).map((p) => p.name));
      setDirty({});
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const merged = useMemo(() => {
    return rows.map((r) => {
      const mf = { ...(r.model_flat || {}), ...(dirty[`${r.project}::model_flat`] || {}) };
      const soh = { ...(r.soh || {}), ...(dirty[`${r.project}::soh`] || {}) };
      return { project: r.project, model_flat: mf, soh };
    });
  }, [rows, dirty]);

  const setCell = (project, kind, month, val) => {
    const num = val === "" ? 0 : parseFloat(val);
    if (Number.isNaN(num)) return;
    setDirty((d) => ({
      ...d,
      [`${project}::${kind}`]: { ...(d[`${project}::${kind}`] || {}), [month]: num },
    }));
  };

  const saveOne = async (project) => {
    const r = merged.find((x) => x.project === project);
    if (!r) return;
    setSaving(true);
    try {
      await api.put("/outflows/epc/model-soh", {
        project,
        model_flat: r.model_flat,
        soh: r.soh,
      });
      toast.success(`Saved ${project}`);
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      // Save any project whose keys appear in dirty
      const projs = new Set(Object.keys(dirty).map((k) => k.split("::")[0]));
      for (const p of projs) {
        const r = merged.find((x) => x.project === p);
        if (r) {
          await api.put("/outflows/epc/model-soh", {
            project: p,
            model_flat: r.model_flat,
            soh: r.soh,
          });
        }
      }
      toast.success(`Saved ${projs.size} project(s)`);
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const addRow = async () => {
    const p = newProject.trim();
    if (!p) return;
    if (rows.some((r) => r.project === p)) {
      toast.error("Row already exists for this project");
      return;
    }
    try {
      await api.put("/outflows/epc/model-soh", { project: p, model_flat: {}, soh: {} });
      setNewProject("");
      setShowAdd(false);
      await load();
    } catch (e) {
      toast.error("Add failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const removeRow = async (project) => {
    if (!window.confirm(`Delete Model Flat / SOH row for "${project}"?`)) return;
    try {
      await api.delete(`/outflows/epc/model-soh/${encodeURIComponent(project)}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const rowTotal = (m) => Object.values(m || {}).reduce((a, b) => a + (parseFloat(b) || 0), 0);
  const dirtyCount = Object.keys(dirty).length;

  return (
    <section className="cf-card p-4 md:p-5" data-testid="epc-model-soh-section">
      <header className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-base text-ink font-bold">EPC — Model Flat &amp; SOH</h3>
          <p className="text-[11px] text-muted-dark">
            Flat ₹ Lakhs per month per project. Model Flat + SOH flow read-only into the Marketing tab.
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button
              data-testid="epc-msoh-add-toggle"
              onClick={() => setShowAdd((s) => !s)}
              className="cf-touch inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface text-ink text-[11px] font-medium border border-hairline hover:bg-surface-2"
            >
              <Plus size={12} /> Add project row
            </button>
            <button
              data-testid="epc-msoh-save-all"
              disabled={saving || dirtyCount === 0}
              onClick={saveAll}
              className="cf-touch inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink text-white text-[11px] font-medium hover:opacity-90 disabled:opacity-50"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save all
            </button>
          </div>
        )}
      </header>

      {showAdd && canEdit && (
        <div className="mb-3 flex items-center gap-2" data-testid="epc-msoh-add-row">
          <select
            value={newProject}
            onChange={(e) => setNewProject(e.target.value)}
            className="text-xs px-2 py-1.5 rounded border border-hairline bg-surface text-ink"
            data-testid="epc-msoh-new-project"
          >
            <option value="">Select project…</option>
            {projects.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <button
            onClick={addRow}
            data-testid="epc-msoh-add-confirm"
            className="cf-touch px-3 py-1.5 rounded-full bg-ink text-white text-[11px] font-medium"
          >
            Add
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-[11px] border-collapse" data-testid="epc-msoh-table">
          <thead>
            <tr>
              <th className="text-left px-2 py-2 uppercase tracking-widest text-[9.5px] text-muted-dark font-semibold sticky left-0 z-20 border-r border-hairline min-w-[180px]" style={{ background: "var(--cf-surface-2)" }}>
                Project · Line
              </th>
              {months?.map((m) => (
                <th key={m} className="text-right px-2 py-2 uppercase tracking-widest text-[9.5px] text-muted-dark font-semibold whitespace-nowrap" style={{ background: "var(--cf-surface-2)" }}>
                  {monthLabel(m)}
                </th>
              ))}
              <th className="text-right px-2 py-2 uppercase tracking-widest text-[9.5px] text-muted-dark font-semibold sticky right-0 z-20 border-l border-hairline" style={{ background: "var(--cf-surface-2)" }}>
                Total
              </th>
              {canEdit && (
                <th className="text-right px-2 py-2 uppercase tracking-widest text-[9.5px] text-muted-dark font-semibold" style={{ background: "var(--cf-surface-2)" }}>
                  ⋯
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {merged.length === 0 && (
              <tr>
                <td colSpan={(months?.length || 0) + 3} className="text-center py-6 text-muted-dark">
                  No Model Flat / SOH entries yet.{canEdit ? " Add a project row above." : ""}
                </td>
              </tr>
            )}
            {merged.map((r) => (
              <>
                <MSohRow key={`${r.project}-mf`} project={r.project} kind="model_flat" label="Model Flat" values={r.model_flat} months={months} onChange={setCell} canEdit={canEdit} />
                <MSohRow key={`${r.project}-soh`} project={r.project} kind="soh" label="SOH" values={r.soh} months={months} onChange={setCell} canEdit={canEdit} onSave={saveOne} onRemove={removeRow} lastOfGroup />
                {/* empty spacer between projects */}
                <tr className="h-1" key={`${r.project}-sep`}><td colSpan={(months?.length || 0) + 3} /></tr>
              </>
            ))}
          </tbody>
        </table>
      </div>

      <footer className="text-[10px] text-muted-light mt-1">
        Amounts in ₹ Lakhs · Totals shown in ₹Cr on Cashflow / Marketing tabs.
      </footer>
    </section>
  );

  function MSohRow({ project, kind, label, values, months, onChange, canEdit, onSave, onRemove, lastOfGroup }) {
    const total = rowTotal(values);
    return (
      <tr className="border-t border-hairline/40" data-testid={`epc-msoh-row-${project}-${kind}`}>
        <td className="px-2 py-1.5 sticky left-0 z-10 border-r border-hairline/40 min-w-[180px]" style={{ background: "var(--cf-surface)" }}>
          <div className="text-ink font-semibold">{project}</div>
          <div className="text-[9.5px] uppercase tracking-widest text-muted-light">{label}</div>
        </td>
        {months?.map((m) => (
          <td key={m} className="px-1 py-1 text-right">
            <input
              type="number"
              step="0.01"
              value={values?.[m] ?? ""}
              disabled={!canEdit}
              onChange={(e) => onChange(project, kind, m, e.target.value)}
              className="w-20 text-right text-[11px] px-1.5 py-0.5 rounded border border-transparent hover:border-hairline focus:border-accent bg-transparent num tabular-nums"
              data-testid={`epc-msoh-cell-${project}-${kind}-${m}`}
              placeholder="–"
            />
          </td>
        ))}
        <td className="px-2 py-1 text-right num tabular-nums font-bold text-ink sticky right-0 z-10 border-l border-hairline/40" style={{ background: "var(--cf-surface)" }}>
          {formatCr(total)}
        </td>
        {canEdit && (
          <td className="px-2 py-1 text-right">
            {lastOfGroup && (
              <div className="flex items-center gap-1 justify-end">
                <button onClick={() => onSave(project)} title="Save this project" data-testid={`epc-msoh-save-${project}`} className="p-1 rounded hover:bg-surface-2">
                  <Save size={12} />
                </button>
                <button onClick={() => onRemove(project)} title="Delete this row" data-testid={`epc-msoh-del-${project}`} className="p-1 rounded hover:bg-surface-2 text-red-500">
                  <Trash2 size={12} />
                </button>
              </div>
            )}
          </td>
        )}
      </tr>
    );
  }
}
